import * as React from "react"
import compose from "recompose/compose"
import { withNavigation } from "@/helpers"
import {
  isUserAuthorized,
  canAccessYourPerformance,
  isAdmin,
} from "@/helpers/authorization"
import { Route, Switch, Redirect } from "react-router-dom"
import { TrackingProvider } from "@/helpers/hoc/withTracking"
import { DateRangeProvider } from "@/helpers/hoc/withDateRange"
import { LightboxProvider } from "@/helpers/hoc/withLightboxTrigger"
import { RouterToUrlQuery } from "react-url-query"
import { LoadingFullScreen } from "@/components"
import { routes } from "@/helpers"
import { withi18n } from "@/helpers/hoc/withi18n"
import { withFeatureFlags } from "@/helpers/hoc/withFeatureFlags"
import { withPermissions } from "@/helpers/hoc/withPermissions"
import { withCurrentUser } from "@/helpers/hoc/withCurrentUser"
import { withUser } from "@/components/UserContext"
import { ModalProvider, ModalRoot } from "@/components/ModalContext"
import {
  NotificationsProvider,
  NotificationsRoot,
} from "@/components/Notifications"
import { RebrandingProvider, createTheme } from "@xingternal/rebranding-adapter"
import { ThemeProvider } from "styled-components"
import customTheme from "@/styles/theme"

const theme = createTheme(customTheme)

const i18n = {
  marketingUrl: "360_RECRUITER_INSIGHTS_MARKETING_URL",
}

const YourPerformance = withNavigation(
  React.lazy(() => import("@/routes/YourPerformance")),
)

const CompanyOverview = withNavigation(
  React.lazy(() => import("@/routes/CompanyOverview")),
)
const Colleagues = withNavigation(
  React.lazy(() => import("@/routes/Colleagues")),
)
const TalentRadar = withNavigation(
  React.lazy(() => import("@/routes/TalentRadar")),
)
const Administration = withNavigation(
  React.lazy(() => import("@/routes/Administration")),
)
const Login = React.lazy(() => import("@/routes/Login"))
const Impersonate = React.lazy(() => import("@/routes/Impersonate"))
const NotFound = React.lazy(() => import("@/routes/NotFound"))

const startPage = ({ enabledFeatures, permissions }) =>
  canAccessYourPerformance({ enabledFeatures, permissions })
    ? routes.yourPerformance
    : routes.companyOverview

const Main = ({
  enabledFeatures,
  enabledFeaturesLoading,
  currentUserLoading,
  userIsLoggedIn,
  sessionIsLoading,
  permissions,
  permissionsLoading,
  marketingUrl,
  canImpersonate,
}) => {
  if (
    sessionIsLoading ||
    currentUserLoading ||
    enabledFeaturesLoading ||
    permissionsLoading
  )
    return <LoadingFullScreen root />

  if (
    userIsLoggedIn &&
    !currentUserLoading &&
    !isUserAuthorized({ enabledFeatures, permissions })
  ) {
    window.location = marketingUrl
    return <LoadingFullScreen root />
  }

  const redirectUrl = window.location.pathname + window.location.search

  const rebranding = {
    enableXingRebranding: enabledFeatures.includes("dev_flag_xing_rebranding"),
  }

  return (
    <RebrandingProvider featureFlags={rebranding}>
      <ThemeProvider theme={theme(rebranding)}>
        {userIsLoggedIn ? (
          <RouterToUrlQuery>
            <TrackingProvider>
              <>
                <DateRangeProvider>
                  <NotificationsProvider>
                    <NotificationsRoot />
                    <ModalProvider>
                      <ModalRoot />
                      <LightboxProvider>
                        <React.Suspense fallback={<LoadingFullScreen />}>
                          <Switch>
                            {/* Your performance */}
                            {canAccessYourPerformance({
                              enabledFeatures,
                              permissions,
                            }) ? (
                              <Route
                                exact
                                path={routes.yourPerformance}
                                component={YourPerformance}
                              />
                            ) : (
                              <Redirect
                                from={routes.yourPerformance}
                                to={routes.companyOverview}
                              />
                            )}
                            {/* Team overview */}
                            <Route
                              exact
                              path={routes.companyOverview}
                              component={CompanyOverview}
                            />
                            {/* Colleagues */}
                            {isAdmin(permissions) ? (
                              <Route
                                exact
                                path={routes.colleagues}
                                component={Colleagues}
                              />
                            ) : (
                              <Redirect
                                from={routes.colleagues}
                                to={routes.yourPerformance}
                              />
                            )}
                            {/* Talent radar */}
                            <Route
                              exact
                              path={routes.talentRadar}
                              component={TalentRadar}
                            />
                            {/* Administration */}
                            {
                              <Route
                                exact
                                path={routes.administration}
                                component={Administration}
                              />
                            }
                            {/* Root */}
                            <Route
                              exact
                              path={routes.root}
                              render={({ location: { search } }) => (
                                <Redirect
                                  to={{
                                    pathname: startPage({
                                      enabledFeatures,
                                      permissions,
                                    }),
                                    search,
                                  }}
                                />
                              )}
                            />
                            {/* Impersonate */}
                            {canImpersonate ? (
                              <Route
                                exact
                                path={routes.impersonate}
                                component={Impersonate}
                              />
                            ) : (
                              <Redirect
                                from={routes.impersonate}
                                to={routes.yourPerformance}
                              />
                            )}
                            {/* Login */}
                            <Redirect from={routes.login} to={routes.root} />
                            <Route path="*" component={NotFound} />
                          </Switch>
                        </React.Suspense>
                      </LightboxProvider>
                    </ModalProvider>
                  </NotificationsProvider>
                </DateRangeProvider>
              </>
            </TrackingProvider>
          </RouterToUrlQuery>
        ) : (
          <React.Suspense fallback={<LoadingFullScreen />}>
            <Switch>
              <Route exact path={routes.login} component={Login} />
              <Redirect
                to={{
                  pathname: routes.login,
                  search: `?dest=${encodeURIComponent(redirectUrl)}`,
                }}
              />
            </Switch>
          </React.Suspense>
        )}
      </ThemeProvider>
    </RebrandingProvider>
  )
}

const enhance = compose(
  withi18n(i18n),
  withUser,
  withCurrentUser,
  withFeatureFlags,
  withPermissions,
)

export default enhance(Main)
